import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Plus, Check, X, Loader2 } from 'lucide-react'
import { getDocuments, getFolderListItems, addFolderListItem, removeFolderListItem } from '../../services/api'
import AggregationToggle from './AggregationToggle'
import { repairMojibake } from '../../utils/textRepair'
import type { FolderListItem } from '../../types'

interface RelatedJournalsPanelProps {
  projectId: string
  folderId: string
  priorArtPaperIds?: Set<string>
  priorArtPaperTitles?: Set<string>
}

interface RelatedJournal {
  journal_id: string
  title: string
  authors?: string[]
  doi?: string
  similarity_score: number
}

function ScoreBadge({ score }: { score: number }) {
  const color = score > 0.8 ? 'bg-green-100 text-green-700' : score > 0.6 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
  return <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${color}`}>{(score * 100).toFixed(0)}%</span>
}

export default function RelatedJournalsPanel({ projectId, folderId, priorArtPaperIds, priorArtPaperTitles }: RelatedJournalsPanelProps) {
  const [aggregate, setAggregate] = useState(false)
  const queryClient = useQueryClient()

  // At project level, folderId === projectId — don't filter by folder to get ALL documents
  const docFolderId = folderId === projectId ? undefined : folderId
  const { data: docsData } = useQuery({
    queryKey: ['documents', projectId, docFolderId],
    queryFn: () => getDocuments(projectId, docFolderId),
  })

  const { data: savedData, isLoading: loadingSaved } = useQuery({
    queryKey: ['folder-list', projectId, folderId, 'journal', aggregate],
    queryFn: () => getFolderListItems(projectId, folderId, 'journal', aggregate),
  })

  const saveMutation = useMutation({
    mutationFn: (journal: RelatedJournal) =>
      addFolderListItem(projectId, folderId, {
        resource_type: 'journal',
        resource_id: journal.journal_id,
        title: journal.title,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['folder-list', projectId, folderId, 'journal'] }),
  })

  const removeMutation = useMutation({
    mutationFn: (item: FolderListItem) =>
      removeFolderListItem(projectId, folderId, 'journal', item.resource_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['folder-list', projectId, folderId, 'journal'] }),
  })

  const savedIds = useMemo(
    () => new Set((savedData?.items || []).map((i) => i.resource_id)),
    [savedData]
  )

  // Aggregate and deduplicate related journals from all documents
  const relatedJournals = useMemo(() => {
    const docs = docsData || []
    const journalMap = new Map<string, RelatedJournal>()

    for (const doc of docs) {
      const related = doc.related_journals
      if (!related) continue
      for (const j of related) {
        const jid = j.id
        const journal: RelatedJournal = {
          journal_id: jid,
          title: repairMojibake(j.title),
          authors: j.authors,
          doi: j.doi,
          similarity_score: j.score ?? 0,
        }
        const existing = journalMap.get(jid)
        if (!existing || journal.similarity_score > existing.similarity_score) {
          journalMap.set(jid, journal)
        }
      }
    }

    // Filter out journals that already appear in prior art
    let journals = Array.from(journalMap.values())
    if (priorArtPaperIds?.size || priorArtPaperTitles?.size) {
      journals = journals.filter((j) => {
        if (priorArtPaperIds?.has(j.journal_id)) return false
        if (priorArtPaperTitles?.has(j.title.toLowerCase())) return false
        return true
      })
    }
    return journals.sort((a, b) => b.similarity_score - a.similarity_score)
  }, [docsData, priorArtPaperIds, priorArtPaperTitles])

  return (
    <div className="space-y-4">
      {/* Related (auto-aggregated) */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Research Landscape</h4>
        {relatedJournals.length === 0 ? (
          <p className="text-sm text-gray-500">No related journals found. Upload documents to generate recommendations.</p>
        ) : (
          <div className="space-y-2">
            {relatedJournals.map((j) => (
              <div key={j.journal_id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{j.title}</p>
                  {j.authors && j.authors.length > 0 && (
                    <p className="text-xs text-gray-500 truncate">
                      {j.authors.slice(0, 3).join(', ')}
                      {j.authors.length > 3 && ` +${j.authors.length - 3}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ScoreBadge score={j.similarity_score} />
                  {j.doi && (
                    <a href={`https://doi.org/${j.doi}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-iflow-500">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {savedIds.has(j.journal_id) ? (
                    <span className="flex items-center gap-0.5 text-xs text-green-600">
                      <Check className="w-3 h-3" /> Saved
                    </span>
                  ) : (
                    <button
                      onClick={() => saveMutation.mutate(j)}
                      disabled={saveMutation.isPending}
                      className="flex items-center gap-0.5 text-xs text-iflow-600 hover:text-iflow-700"
                    >
                      <Plus className="w-3 h-3" /> Save
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Saved Journals */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Saved Journals</h4>
          <AggregationToggle projectId={projectId} folderId={folderId} value={aggregate} onChange={setAggregate} />
        </div>
        {loadingSaved ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
        ) : (savedData?.items || []).length === 0 ? (
          <p className="text-sm text-gray-500">No saved journals yet. Save from the related list above.</p>
        ) : (
          <div className="space-y-2">
            {(savedData?.items || []).map((item) => (
              <div key={item.sort_key} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{item.title}</p>
                  {item.source_folder_id && (
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">
                      from child folder
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removeMutation.mutate(item)}
                  disabled={removeMutation.isPending}
                  className="p-1 text-gray-500 hover:text-red-500 rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
